import { PropsProject } from "pages/Projects/type";

// Config
import config from "lib/config";

// Type
type PropsAddresses = {
  [index: string]: string | number | boolean;
}

type TGetImgUrl = {
  w?: number;
  h?: number;
  id?: string;
};
type TGetValueSize = {
  options: { [index: string]: string | number }[];
  opt: string | number;
};
export type TPriceGroup = { supplier: string; prices: any[] } | { supplier: string; prices: [] };

export function waitForElement(selector: string, timeout = 2000): Promise<Element | null> {
  return new Promise((resolve) => {
    const element = document.querySelector(selector);
    if (element) return resolve(element);

    const observer = new MutationObserver(() => {
      const el = document.querySelector(selector);
      if (el) {
        observer.disconnect();
        resolve(el);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    setTimeout(() => {
      observer.disconnect();
      resolve(null);
    }, timeout);
  });
}

/**
 * Find supplier group by key with safe fallback.
 */
export function findSupplierGroup(prices: any[] | undefined, supplierKey?: string, defaultSupplier = 'EBC'): TPriceGroup {
  if (!Array.isArray(prices) || prices.length === 0) return { supplier: defaultSupplier, prices: [] };
  const key = supplierKey ?? prices[0]?.supplier;
  return prices.find(p => p?.supplier === key) || prices[0] || { supplier: defaultSupplier, prices: [] };
}

export function debounce<T extends (...args: any[]) => void>(func: T, wait: number) {
  let timeout: ReturnType<typeof setTimeout> | null;

  const debounced = (...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => {
      func(...args);
    }, wait);
  };

  debounced.cancel = () => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
  };

  debounced.flush = (...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
      func(...args);
    }
  };

  return debounced as T & { cancel: () => void; flush: (...args: Parameters<T>) => void };
}

export const isDev = process.env.REACT_APP_NODE_ENV === "development";

export const formatWeight = (value: number): string => {
  if (value < 1000) {
    return `${value} mg`;
  }

  const grams = (value / 1000).toFixed(2).replace(/\.00$/, "");
  return `${grams} g`;
};

export const convertToUniqueIdArray = (input: string): string[] => {
  const validIdRegex = /^(EBC-\d+|\d{2,7}-\d{2}-\d|\w\d{5,})$/i;

  return [...new Set(
    input
      .trim()
      .split(/[\s,.]+/)
      .map(id => id.replace(/[?<>!@#:;|$%^&*()[\]{}\\/]/g, ''))
      .filter(Boolean)
      .filter((id) => validIdRegex.test(id))
  )];
};

export const getValueSize = (param: TGetValueSize) => param?.options?.find(
  (x) => x.value === param.opt
);

export const getImgUrl = (param: TGetImgUrl) => {
  const { w = 550, h = 550, id = "" } = param;
  return `${config.api.apiUrl}ebc/catalog/${id}/image?format=png:-a,w${w},h${h},noantialias,H_hetero`;
};

export const patternRxpCheckEng = /^[A-Za-z0-9 !@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+$/;

export const isEngLang = (value: string) => {
  if (!value) return true;

  const lines = value.trim().split(/\s+/); // разделим по пробелам и переносам
  return lines.every(line => patternRxpCheckEng.test(line));
};

export const filteredDataByKey = ({ data, key }: { data?: string[], key?: string }) => {
  return data?.length ? data.filter((item: string) => item === key) : [];
};

export const hexEncode = (
  instr: string,
  withoutZero: boolean = false
) => {
  let hex, i;
  let result = "";

  if (instr) {
    for (i = 0; i < instr.length; i++) {
      hex = instr.charCodeAt(i).toString(16);
      result += (!withoutZero ? "000" : "" + hex).slice(-4);
    }
  }

  return result;
};

export const decodeHtmlEntity = (text: string) => {
  if (text && typeof text === "string") {
    return text.replace(/&#(\d+);/g, function(match: string, dec) {
      return String.fromCharCode(dec);
    });
  } else {
    return text;
  }
};

export const percentage = (partialValue: number, maxValue: number) =>
  (100 * partialValue) / maxValue;

export const compareSort = (keyName: string = "") => (a: any, b: any) => {
  const nameA = a[keyName]?.replace(/(<([^>]+)>)/gi, "")?.toUpperCase(); // ignore upper and lowercase
  const nameB = b[keyName]?.replace(/(<([^>]+)>)/gi, "")?.toUpperCase(); // ignore upper and lowercase

  if (nameA < nameB) {
    return -1;
  }
  if (nameA > nameB) {
    return 1;
  }
  // names must be equal
  return 0;
};

/**
 * Return formated amount.
 *
 * @param {String} amount - amount
 * @param {String} currency - currency symbol (optional)
 * @param {number} minimumFractionDigits - count minimum digits ni number
 * @returns {String} - formated string
 */
export function amountFormat(amount: number, currency: string = "usd", minimumFractionDigits: number = 2) {
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: minimumFractionDigits
  });
  return formatter.format(amount);
}

export const isNumber = (n: any) => {
  return !isNaN(parseFloat(n)) && !isNaN(n - 0);
};

/**
 * Return convert to array.
 *
 * @returns {array}
 * @param data
 */
export const convertedToArray = (data: any) => Array.isArray(data)
  ? data
  : Object.values(data);

/**
 * Return count item in current project.
 *
 * @returns {number} - count in current project
 * @param data
 * @param currentProject
 */
export const calcCountItems = (data: any, currentProject: number) => {
  if (!currentProject) return 0;

  const convertedProjects = Array.isArray(data) ? data : convertedToArray(data);

  const findCurrProject = convertedProjects.find(
    (project: PropsProject) => project?.id === currentProject
  );

  if (!findCurrProject) return 0;

  const projectItemLen = findCurrProject.items.filter(
    (item: any) => item.selected
  ).filter(Boolean);

  return projectItemLen.length;
};

export function formatDate(inputDate?: string, isTimeFirst = false) {
  const date = new Date(inputDate || "");

  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const seconds = date.getSeconds().toString().padStart(2, "0");

  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0"); // Note: Month is 0-based

  const year = date.getFullYear();

  return isTimeFirst
    ? `${hours}:${minutes}:${seconds}, ${day}.${month}.${year}`
    : `${day}.${month}.${year}, ${hours}:${minutes}:${seconds}`;
}

/**
 * Convert data Addresses by type
 * @param data
 * @param type
 */
export const convertDataAddresses = (data: PropsAddresses[], type: string) =>
  data.length
    ? data.filter((x: PropsAddresses) => x?.type === type)
    : [];

/**
 * Convert data Addresses by type
 * @param data
 * @param type
 * @param key
 */
export const foundDataAddresses = (data: PropsAddresses[], key: string, type: string | number) =>
  data.length ? data.find((item: PropsAddresses) => item[key] === type) : {};

/**
 * Convert data prices
 * @param data
 */
export const convertDataPrices = (data: any) => {
  return data?.length ? data?.map((item: any) => ({
    mg: item.mg,
    available: item.available,
    value: `price${item.mg}mg`,
    label: `${item.mg}mg`,
    priceUsd: item?.priceUsd ? item?.priceUsd : "POA",
    priceEur: item?.priceEur ? item?.priceEur : "POA",
    price: item?.priceUsd ? item?.priceUsd : "POA",
  })) : [];
};

/**
 * Convert img
 * @param {string} item
 * @param {object} param
 * @returns {String} - String img
 */
export const getImg = (
  item: string,
  param?: { [index: string]: string | number }
) => {
  let src = "";

  if (item) {
    const smilesHex = hexEncode(item, true);

    src = `${
      config.api.apiUrl
    }chemutils/smiles-image?smiles=${smilesHex}&format=png:w${param?.w || 90},h${
      param?.h || 90
    },noantialias,H_hetero`;
  }

  return src;
};

/**
 * Convert array prices
 * @param {array} data - prices array
 * @returns {array} - New array
 */
export const filteredDataPrices = (data: { [index: string]: string | number | boolean }[]) => {
  return data?.length ? data?.filter(
    (x: { [index: string]: string | number | boolean }) => x.mg !== 15
  ) : [];
};

/**
 * Convert projects array to options
 * @param {array} data - Data projects array
 * @returns {array} - New array convert options
 */
export const convertProjectsToOptions = (data: any) => data?.length ? data?.map(
  (x: any) => ({ label: x?.name, value: x?.id })
) : [];

/**
 * Convert options prices
 * @param {object} item - object option
 * @returns {object} - new format option
 */
export const optionsPrices = (item: { [index: string]: string | number | boolean }) => {
  return {
    label: `$${item.priceUsd}/${item.mg}mg`,
    value: item.priceUsd,
    mg: item.mg
  };
};

export const truncate = (str: string, max: number) => {
  return str.length > max ? str.substring(0, max) + "..." : str;
};


export const generateXLSTableSave = async (args: { data: any; headers: any; cb?: any }) => {
  const ExcelJS = await import('exceljs');
  const { saveAs } = await import('file-saver');

  const { data, headers, cb } = args;

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("EBC", {
    properties: { defaultRowHeight: 15 },
    pageSetup: {
      paperSize: 9,
      orientation: "portrait",
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0
    },
    views: [{ zoomScale: 85, zoomScaleNormal: 100 }]
  });

  worksheet.columns = headers.map((h: any) => ({ header: h.data, key: h.data, width: 32 }));
  worksheet.getRow(1).font = { size: 14, bold: true };

  const s = document.createElement("p");
  const totalRecords = data.length;

  data.forEach((rowData: any, i: number) => {
    const row: Record<string, string> = {};
    headers.forEach((h: any) => {
      s.innerHTML = rowData[h.data] ? decodeHtmlEntity(rowData[h.data]) : "";
      row[h.data] = s.textContent || "";
    });
    worksheet.addRow(row);
    cb?.(Math.floor(((i + 1) * 100) / totalRecords));
  });

  const date = formatDate(new Date().toString(), true);

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), `${date}_POA.xlsx`);
};